import { discussionKeepMs } from "./discussion";
import { parseDiscussionResult, unavailable, type DiscussionResult, type DiscussionWindow } from "./discussionWindow";

export const goodDiscussion = (result: DiscussionResult | undefined): DiscussionWindow | undefined => result?.kind === "window" ? result.window : result?.cached;
export const keepDiscussion = (result: DiscussionResult, previous?: DiscussionResult): DiscussionResult => result.kind === "unavailable" && result.reason !== "disabled" && goodDiscussion(previous) ? { ...result, cached: goodDiscussion(previous) } : result;

/** Per-article generations protect both stored data and in-flight request identity. */
export class DiscussionCache {
  private kept = new Map<string, { at: number; result: DiscussionResult }>();
  private flying = new Map<string, Promise<DiscussionResult>>();
  private revisions = new Map<string, string | null>();
  constructor(private readonly now = Date.now, private readonly capacity = 20) {}
  load(article: string, cursor: string | undefined, run: () => Promise<DiscussionResult>, fresh = false): Promise<DiscussionResult> {
    const prefix = `${article}\u0000`, key = prefix + (cursor ?? "");
    if (fresh) {
      for (const k of this.flying.keys()) if (k.startsWith(prefix)) this.flying.delete(k);
    }
    const previous = this.kept.get(key);
    const state = previous?.result.kind === "window" ? previous.result.window.state : previous?.result.reason;
    if (!fresh && previous && this.now() - previous.at < discussionKeepMs(state ?? "failed")) {
      this.kept.delete(key); this.kept.set(key, previous); return Promise.resolve(previous.result);
    }
    const pending = this.flying.get(key); if (pending) return pending;
    const request = Promise.resolve().then(run).then(value => parseDiscussionResult(value) ?? unavailable("failed", goodDiscussion(previous?.result)?.link ?? "", this.now()), () => unavailable("failed", goodDiscussion(previous?.result)?.link ?? "", this.now())).then(result => {
      if (this.flying.get(key) !== request) return result;
      if ((!cursor && result.kind === "window") || (result.kind === "unavailable" && result.reason === "disabled")) {
        for (const k of this.kept.keys()) if (k.startsWith(prefix)) this.kept.delete(k);
        for (const k of this.flying.keys()) if (k !== key && k.startsWith(prefix)) this.flying.delete(k);
      }
      const kept = keepDiscussion(result, previous?.result);
      this.kept.delete(key); this.kept.set(key, { at: this.now(), result: kept });
      for (const k of this.kept.keys()) { if (this.kept.size <= this.capacity) break; this.kept.delete(k); }
      return kept;
    }).finally(() => { if (this.flying.get(key) === request) this.flying.delete(key); });
    this.flying.set(key, request); return request;
  }
  forget(article: string) {
    for (const key of this.kept.keys()) if (key.startsWith(`${article}\u0000`)) this.kept.delete(key);
    for (const key of this.flying.keys()) if (key.startsWith(`${article}\u0000`)) this.flying.delete(key);
  }
  /** One bounded metadata subscription invalidates copies without fetching their cold JSON. */
  revise(article: string, revision: string | null): boolean {
    const known = this.revisions.has(article), previous = this.revisions.get(article);
    const changed = known ? previous !== revision : [...this.kept.keys(), ...this.flying.keys()].some(key => key.startsWith(`${article}\u0000`));
    if (changed) this.forget(article);
    this.revisions.delete(article); this.revisions.set(article, revision);
    for (const key of this.revisions.keys()) { if (this.revisions.size <= this.capacity) break; this.revisions.delete(key); }
    return changed;
  }
  clear() { this.kept.clear(); this.flying.clear(); this.revisions.clear(); }
}
